import clsx from "clsx";
import { useState } from "react";
import { initials } from "../lib/format";

/**
 * La ficha redonda del jugador: su foto, y sus iniciales cuando no la hay.
 *
 * POR QUÉ HAY FOTO (enmienda de la §8.4, 30-08-2026, por decisión del propietario)
 *   La §8.4 dejaba la ficha redonda con las iniciales en Barlow Condensed, y así está en los
 *   mockups de *Plantilla* y *Mercado*. Con la plantilla y el mercado llenos, dos iniciales no
 *   distinguen a nadie: «Le Normand» y «Lewandowski» son los dos `LE`. La foto se pone **solo
 *   donde los mockups ya dibujaban esa ficha redonda** —las listas y la fila de identidad de la
 *   ficha—, con el mismo tamaño, el mismo borde y el mismo hueco.
 *
 *   **En el campo no.** Ahí la §8.4 sigue mandando: cada jugador es un dorsal sobre una ficha
 *   clara. No es purismo; once caras a 30 px sobre el césped se distinguen peor que once números.
 *
 * LA URL ES UNA CONVENCIÓN, Y POR ESO HAY RESPALDO
 *   Biwenger **no manda la foto en los datos**: de los 565 jugadores del catálogo, ninguno trae un
 *   campo de imagen. Lo único parecido es `iconHero` (`i/p/hero/{id}.png`), y solo lo llevan 91.
 *   Así que la URL se arma desde el id. Del catálogo constan el host, el optimizador
 *   (`config.optimizeImages`) y el prefijo `i/p/`; el nombre del retrato pequeño **no está
 *   observado en ningún fixture**.
 *
 *   De ahí que esto no sea un `<img>` a secas: si la imagen no llega —porque la convención cambie,
 *   porque ese jugador no tenga retrato o porque no haya internet—, se vuelve a las iniciales y la
 *   lista se ve exactamente igual que antes de esta enmienda. Nunca un icono roto.
 *
 * NO SALE NI UN DATO NUESTRO
 *   La petición la hace el navegador contra un CDN público: no lleva el token (el cliente no lo
 *   tiene nunca), ni cabeceras de sesión, ni identificadores de liga o usuario. El candado de red
 *   del servidor —`mode: offline`/`live`— no interviene aquí porque el servidor no participa.
 */

/** Host y optimizador de imágenes de Biwenger, ambos observados en `catalog.json`. */
const CDN = "https://cdn.biwenger.com/cdn-cgi/image/f=avif";

/**
 * La foto de un jugador a partir de su id.
 *
 * Está aquí sola para que, si algún día la convención cambia, se cambie en un único sitio. El
 * prefijo `i/p/` es el de `iconHero`; lo que no consta en ninguna captura es que el retrato
 * pequeño sea `{id}.png`. Si no lo fuera, esta función es lo único que hay que tocar.
 */
export function playerPhotoUrl(playerId: number): string {
  return `${CDN}/i/p/${String(playerId)}.png`;
}

export interface PlayerAvatarProps {
  playerId: number;
  /** Solo para las iniciales de respaldo y el texto alternativo. */
  name: string;
  /** Lado del círculo en píxeles. 38 es el de los mockups; el recomendador usa 28. */
  size?: number;
  className?: string;
}

/** El tamaño de los mockups (`estilo.css`, `.player .avatar`). */
const DEFAULT_SIZE = 38;

export function PlayerAvatar({
  playerId,
  name,
  size = DEFAULT_SIZE,
  className,
}: PlayerAvatarProps) {
  const [sinFoto, setSinFoto] = useState(false);

  /*
   * `aria-hidden` en los dos casos: el nombre del jugador va siempre escrito al lado, así que un
   * lector de pantalla que anunciara además la foto o las iniciales diría dos veces lo mismo. Es
   * lo que hacía la ficha de iniciales antes de esta enmienda y no cambia.
   */
  const comunes = {
    "aria-hidden": true as const,
    style: { width: size, height: size },
    className: clsx(
      "shrink-0 rounded-full border border-border bg-white/5",
      className,
    ),
  };

  if (sinFoto) {
    return (
      <span
        {...comunes}
        data-testid="player-avatar-initials"
        className={clsx(
          comunes.className,
          "grid place-items-center font-display font-semibold text-muted",
        )}
        style={{ ...comunes.style, fontSize: Math.round(size * 0.4) }}
      >
        {initials(name)}
      </span>
    );
  }

  return (
    <img
      {...comunes}
      src={playerPhotoUrl(playerId)}
      alt=""
      width={size}
      height={size}
      /* Las listas pintan cientos de filas: sin `lazy` se pedirían todas las fotos de golpe. */
      loading="lazy"
      decoding="async"
      /* El hueco ya está reservado por `width`/`height`, así que la foto no empuja nada al
         llegar: los skeletons de la §8.2 no se mueven bajo el dedo. */
      className={clsx(comunes.className, "object-cover")}
      onError={() => {
        setSinFoto(true);
      }}
    />
  );
}
